import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  forgetAccount,
  getRememberedAccounts,
  isDevHost,
  type RememberedAccount
} from '../auth/session';
import { Avatar } from '../components/Avatar';
import { BootScreen } from '../components/BootScreen';
import { IconGoogle } from '../components/icons';
import '../styles/login.scss';

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-̟]/g, '')
    .replace(/ł/g, 'l')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { user, initializing, firebaseEnabled, loginDev, loginGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const denied = searchParams.get('denied') === '1';
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState<RememberedAccount[]>(() => getRememberedAccounts());
  const [devName, setDevName] = useState('');

  if (initializing) return <BootScreen />;
  if (user) return <Navigate to="/app/today" replace />;

  const showDevLogin = isDevHost();

  const handleGoogle = async () => {
    setError(false);
    setBusy(true);
    try {
      await loginGoogle();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const handleAccount = (account: RememberedAccount) => {
    if (account.kind === 'dev') {
      loginDev(account);
    } else {
      void handleGoogle();
    }
  };

  const handleForget = (uid: string) => {
    forgetAccount(uid);
    setAccounts(getRememberedAccounts());
  };

  const handleAddDev = () => {
    const name = devName.trim();
    if (!name) return;
    const slug = slugify(name);
    loginDev({ uid: `dev-${slug}`, email: `${slug}@dev.local`, name });
  };

  return (
    <div className="login-page">
      <div className="login-hero">
        <div className="brand-row">
          <span className="brand-mark" />
          <span className="brand-name">{t('app.name')}</span>
        </div>
        <div className="login-hero-text">
          <div className="login-kicker">{t('login.kicker')}</div>
          <h1 className="login-title">{t('login.title')}</h1>
          <p className="login-subtitle">{t('login.subtitle')}</p>
        </div>
      </div>
      <div className="login-side">
        <div className="login-card">
          {denied && <div className="login-error">{t('login.noAccess')}</div>}
          {firebaseEnabled && (
            <button
              type="button"
              className="btn login-google"
              onClick={() => void handleGoogle()}
              disabled={busy}
            >
              <IconGoogle />
              {t('login.google')}
            </button>
          )}
          {error && <div className="login-error">{t('login.googleError')}</div>}
          {accounts.length > 0 && (
            <>
              <div className="login-divider">{t('login.rememberedSection')}</div>
              {accounts
                .filter((account) => account.kind !== 'dev' || showDevLogin)
                .map((account) => (
                  <div key={account.uid} className="login-account-row">
                    <button
                      type="button"
                      className="btn login-dev-btn"
                      onClick={() => handleAccount(account)}
                    >
                      <Avatar name={account.name} />
                      <span>
                        {account.name}
                        <small>{account.kind === 'dev' ? t('login.devBadge') : account.email}</small>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="login-forget"
                      title={t('login.forget')}
                      onClick={() => handleForget(account.uid)}
                    >
                      ×
                    </button>
                  </div>
                ))}
            </>
          )}
          {showDevLogin && (
            <>
              <div className="login-divider">{t('login.devSection')}</div>
              <div className="login-dev-add">
                <input
                  type="text"
                  value={devName}
                  placeholder={t('login.devNamePlaceholder')}
                  onChange={(e) => setDevName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddDev();
                  }}
                />
                <button type="button" className="btn" onClick={handleAddDev}>
                  {t('login.devAdd')}
                </button>
              </div>
              <div className="login-dev-hint">{t('login.devHint')}</div>
            </>
          )}
        </div>
        <a className="login-setup-link" href={"/setup.html?v=" + (process.env.BUILD_ID ?? "")}>
          {t('login.setupLink')}
        </a>
      </div>
    </div>
  );
}
